import { APP_CONFIG } from '../../shared/config/app.config';

/**
 * Sesión del usuario contra GoTrue: access token (JWT) + refresh token.
 *
 * - Se persiste en localStorage para sobrevivir a recargas y a la PWA cerrada.
 * - El access token se renueva antes de caducar (timer + al volver a la
 *   pestaña, porque los timers se congelan en segundo plano).
 * - GoTrue rota el refresh token en cada uso, así que varias pestañas no
 *   deben refrescar a la vez: se serializa con Web Locks y, dentro del lock,
 *   se reutiliza la sesión si otra pestaña ya la renovó.
 */

export interface Session {
  accessToken: string;
  refreshToken: string;
  /** Epoch ms en que caduca el access token. */
  expiresAt: number;
}

export interface SessionUser {
  id: string;
  email: string | null;
}

type Listener = (session: Session | null) => void;

const STORAGE_KEY = 'gym-full.session';
const LOCK_NAME = 'gym-full.session-refresh';
/** Margen para renovar antes de que caduque el access token. */
const REFRESH_MARGIN_MS = 60_000;
/**
 * Espera mínima entre reintentos cuando el refresco falla por algo temporal.
 * Sin ella el token ya caducado programaría el siguiente intento a 0 ms y el
 * reintento se convertiría en un bucle de timers.
 */
const REFRESH_RETRY_MS = 30_000;

const AUTH_URL = APP_CONFIG.AUTH.URL;

let current: Session | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let inFlightRefresh: Promise<Session | null> | null = null;
const listeners = new Set<Listener>();

function readStored(): Session | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session>;
    if (parsed.accessToken && parsed.refreshToken && typeof parsed.expiresAt === 'number') {
      return parsed as Session;
    }
  } catch {
    // almacenamiento no disponible o corrupto: sin sesión
  }
  return null;
}

function writeStored(session: Session | null) {
  try {
    if (session) globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(session));
    else globalThis.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // sin almacenamiento la sesión dura lo que la pestaña
  }
}

function scheduleRefresh() {
  clearTimeout(refreshTimer);
  if (!current) return;
  const untilRefresh = current.expiresAt - Date.now() - REFRESH_MARGIN_MS;
  // Si ya toca renovar es que el intento anterior falló: se espera antes de
  // volver a probar en vez de reintentar sin pausa.
  refreshTimer = setTimeout(
    () => void refreshSession(),
    untilRefresh > 0 ? untilRefresh : REFRESH_RETRY_MS,
  );
}

function setSession(session: Session | null, { persist = true } = {}) {
  const changed = current?.accessToken !== session?.accessToken;
  current = session;
  if (persist) writeStored(session);
  scheduleRefresh();
  if (changed) listeners.forEach((listener) => listener(session));
}

function needsRefresh(session: Session): boolean {
  return session.expiresAt - Date.now() < REFRESH_MARGIN_MS;
}

/** Respuesta de sesión de GoTrue (`/verify`, `/token`). */
export interface GoTrueSessionResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export function sessionFromResponse(body: GoTrueSessionResponse): Session {
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token,
    expiresAt: Date.now() + body.expires_in * 1000,
  };
}

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = globalThis.navigator?.locks;
  return locks ? locks.request(LOCK_NAME, fn) : fn();
}

/**
 * Fallo temporal (sin red, 429, 5xx): la guardada sigue siendo la mejor sesión
 * que tenemos, así que se conserva y se reintenta luego. En el arranque en frío
 * `current` aún es null, de ahí que haya que publicarla aquí.
 */
function keepForRetry(stored: Session): Session {
  if (!current) setSession(stored, { persist: false });
  return current ?? stored;
}

export interface RefreshOptions {
  /**
   * Access token que el backend acaba de rechazar con 401. Fuerza la
   * renovación contra GoTrue aunque por fecha aún pareciera vigente: la hora
   * del móvil puede ir mal, el timer pudo quedarse dormido en segundo plano o
   * el backend pudo revocarlo. Si otra pestaña ya lo cambió, se adopta el
   * suyo sin llamar.
   */
  rejectedToken?: string;
}

async function doRefresh({ rejectedToken }: RefreshOptions = {}): Promise<Session | null> {
  return withRefreshLock(async () => {
    // Otra pestaña pudo renovarla mientras esperábamos el lock
    const stored = readStored();
    if (!stored) {
      setSession(null, { persist: false });
      return null;
    }
    const alreadyRenewed = rejectedToken !== undefined && stored.accessToken !== rejectedToken;
    if (alreadyRenewed || (rejectedToken === undefined && !needsRefresh(stored))) {
      setSession(stored, { persist: false });
      return stored;
    }

    let response: Response;
    try {
      response = await fetch(`${AUTH_URL}/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: stored.refreshToken }),
      });
    } catch {
      // Sin red: mantenemos la sesión y reintentamos al volver
      return keepForRetry(stored);
    }

    // 4xx: refresh token revocado, caducado o ya usado → hay que volver a entrar.
    // 429 / 5xx: fallo temporal → se conserva la sesión y se reintenta.
    if (response.status >= 400 && response.status < 500 && response.status !== 429) {
      setSession(null);
      return null;
    }
    if (!response.ok) return keepForRetry(stored);

    const session = sessionFromResponse((await response.json()) as GoTrueSessionResponse);
    setSession(session);
    return session;
  });
}

/**
 * Renueva la sesión (una sola llamada aunque la pidan varios a la vez: el
 * refresh token solo vale una vez). Devuelve null solo si no hay sesión o si
 * GoTrue la ha rechazado; ante un fallo temporal devuelve la que había.
 */
export function refreshSession(options: RefreshOptions = {}): Promise<Session | null> {
  inFlightRefresh ??= doRefresh(options).finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

/** Carga la sesión guardada, renovándola si está a punto de caducar. */
export async function initSession(): Promise<Session | null> {
  const stored = readStored();
  if (!stored) {
    setSession(null, { persist: false });
    return null;
  }
  // Un access token ya caducado no se publica: al notificar a los listeners,
  // AuthContext pedía /users/me con él, el backend devolvía 401 y getMyProfile
  // borraba la sesión — incluido el refresh token, que seguía siendo válido.
  // Esa carrera (el 401 llegaba antes que el refresh) deslogueaba al usuario
  // cada vez que reabría la app pasada una hora. Aquí se espera al refresco y
  // solo se publica el resultado.
  if (needsRefresh(stored)) return refreshSession();
  setSession(stored, { persist: false });
  return stored;
}

export function saveSession(session: Session) {
  setSession(session);
}

export function clearSession() {
  setSession(null);
}

export function getSession(): Session | null {
  return current;
}

export function onSessionChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Lee `sub` y `email` del JWT (sin verificar: eso lo hacen GoTrue y el backend). */
export function userFromAccessToken(accessToken: string): SessionUser | null {
  try {
    const payload = accessToken.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { sub?: string; email?: string };
    return claims.sub ? { id: claims.sub, email: claims.email ?? null } : null;
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && current && needsRefresh(current)) {
      void refreshSession();
    }
  });
  // Login/logout/renovación en otra pestaña
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) setSession(readStored(), { persist: false });
  });
}
